import { useEffect, useMemo } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import * as R from 'ramda';

import { GET_MATERIAL_ENTRIES } from '@atom/graph/materialEntry';
import { GET_TASK_LOCATIONS } from '@atom/graph/taskLocation';
import { GET_TIME_ENTRIES } from '@atom/graph/timeEntry';
import { GET_WORK_ORDER, GET_WORK_REPORTS } from '@atom/graph/work';
import { useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import {
  MaterialEntriesConnection,
  MaterialEntriesConnectionInput,
} from '@atom/types/materialEntry';
import {
  TaskLocationsConnection,
  TaskLocationsConnectionInput,
} from '@atom/types/taskLocation';
import {
  TimeEntriesConnectionInput,
  WorkOrderTimeEntriesConnection,
} from '@atom/types/timeEntry';
import {
  WorkOrderDetailType,
  WorkOrderType,
  WorkReportsConnection,
} from '@atom/types/work';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import { isWorkOrderCompletedOrAbove } from '@atom/utilities/workOrderStatusUtilities';

import { EMPTY_TASK } from './reportConstants';
import {
  getLocationsDictionary,
  getReportAssetsDictionary,
  getReportMaterialEntriesDictionary,
  getReportUsersDictionary,
  getWorkReportDictionary,
} from './reportUtilities';
import TaskDetailReport from './TaskDetailReport';

import classes from './workOrderReport.module.css';

const AldotWorkOrderPdfReport = () => {
  const [, { params }] = useNavigation();
  const { workOrderId } = params;
  const preferences = usePreferences();

  const {
    data: workOrderData,
    loading: workOrderLoading,
    error: workOrderError,
  } = useQuery<{ workOrder: WorkOrderDetailType }, { id: string }>(
    GET_WORK_ORDER,
    {
      variables: {
        id: workOrderId,
      },
      fetchPolicy: 'network-only',
    },
  );

  const {
    data: workReportsData,
    loading: workReportsLoading,
    error: workReportsError,
  } = useQuery<{ workReports: WorkReportsConnection }, { id: string }>(
    GET_WORK_REPORTS,
    {
      variables: {
        id: workOrderId,
      },
      fetchPolicy: 'network-only',
    },
  );

  const {
    data: timeEntriesData,
    loading: timeEntriesLoading,
    error: timeEntriesError,
  } = useQuery<
    { timeEntries: WorkOrderTimeEntriesConnection },
    { input: TimeEntriesConnectionInput }
  >(GET_TIME_ENTRIES, {
    variables: {
      input: {
        workOrderId,
      },
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: materialEntriesData,
    loading: materialEntriesLoading,
    error: materialEntriesError,
  } = useQuery<
    { materialEntries: MaterialEntriesConnection },
    { input: MaterialEntriesConnectionInput }
  >(GET_MATERIAL_ENTRIES, {
    variables: {
      input: {
        workOrderId,
      },
    },
    fetchPolicy: 'network-only',
  });

  const [getTaskLocations, { data: locationsData, loading: locationsLoading }] =
    useLazyQuery<
      { taskLocations: TaskLocationsConnection },
      { input: TaskLocationsConnectionInput }
    >(GET_TASK_LOCATIONS, {
      fetchPolicy: 'no-cache',
    });

  const workOrder = workOrderData?.workOrder;
  const hasNoTasks = isNilOrEmpty(workOrderData?.workOrder?.tasks);
  const timeEntries = timeEntriesData?.timeEntries?.timeEntries;
  const materialEntries = materialEntriesData?.materialEntries?.materialEntries;
  const isCompletedOrAbove = isWorkOrderCompletedOrAbove(workOrder?.statusId);
  const aldotWorkReports = workReportsData?.workReports?.workReports;

  useEffect(() => {
    if (workOrder?.type === WorkOrderType.TASK_ASSETS_AND_LOCATIONS) {
      const ids = workOrder?.tasks?.reduce((acc, task) => {
        return [...acc, ...task?.locations.map(location => location.id)];
      }, []);

      getTaskLocations({ variables: { input: { ids } } });
    }
  }, [workOrder]);

  const locations = locationsData?.taskLocations?.taskLocations;

  const reportLocationsDictionary = useMemo(() => {
    return getLocationsDictionary(locations, workOrder);
  }, [locations, workOrder]);

  const reportUsersDictionary = useMemo(() => {
    return getReportUsersDictionary(timeEntries, workOrder, preferences);
  }, [timeEntries, workOrder]);

  const reportMaterialEntriesDictionary = useMemo(() => {
    return getReportMaterialEntriesDictionary(materialEntries, workOrder);
  }, [materialEntries, workOrder]);

  const aldotWorkReportDictionary = useMemo(() => {
    return getWorkReportDictionary(aldotWorkReports, workOrder);
  }, [aldotWorkReports, workOrder]);

  const reportAssetsDictionary = useMemo(() => {
    return getReportAssetsDictionary(workOrder);
  }, [workOrder]);

  const showLoaded =
    !workOrderLoading &&
    !workOrderError &&
    !timeEntriesLoading &&
    !timeEntriesError &&
    !materialEntriesLoading &&
    !materialEntriesError &&
    !workReportsLoading &&
    !workReportsError &&
    !locationsLoading;

  return (
    <div className={classes.container}>
      {hasNoTasks ? (
        <TaskDetailReport
          key={EMPTY_TASK.id}
          // @ts-ignore
          task={EMPTY_TASK}
          type={workOrder?.type}
          isCompletedOrAbove={false}
          reportUsers={[]}
          reportMaterials={{ startEndMaterials: [], usageMaterials: [] }}
          reportLocations={[]}
          reportAssets={[]}
          lastTask
        />
      ) : (
        workOrder?.tasks?.map((task, index) => {
          const lastTask = R.length(workOrder?.tasks) === index + 1;

          return (
            <TaskDetailReport
              key={task?.id}
              task={task}
              type={workOrder?.type}
              isCompletedOrAbove={isCompletedOrAbove}
              reportUsers={reportUsersDictionary[task?.id]}
              reportMaterials={reportMaterialEntriesDictionary[task?.id]}
              reportAssets={reportAssetsDictionary[task?.id]}
              aldotWorkReport={aldotWorkReportDictionary[task?.id]}
              reportLocations={reportLocationsDictionary[task?.id]}
              lastTask={lastTask}
            />
          );
        })
      )}
      {showLoaded && <div className={classes.loaded} id="loaded" />}
    </div>
  );
};

export default AldotWorkOrderPdfReport;
